import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const emptyCompany = {
  name: "",
  taxNumber: "",
  vatNumber: "",
  companyRegistryNumber: "",
};

export default function RegisterPage() {
  const { register, user, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    password2: "",
    firstName: "",
    lastName: "",
    phone: "",
    isCompany: false,
    company: emptyCompany,
  });
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to="/fiok" replace />;
  }

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setCompany = (key, value) =>
    setForm((f) => ({ ...f, company: { ...f.company, [key]: value } }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (form.password !== form.password2) {
      setError("A két jelszó nem egyezik.");
      return;
    }
    setBusy(true);
    try {
      const data = await register({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone || undefined,
        isCompany: form.isCompany,
        company: form.isCompany ? form.company : undefined,
      });
      setInfo(data.message || "Sikeres regisztráció.");
      navigate("/fiok", { replace: true });
    } catch (err) {
      setError(err.message || "Regisztráció sikertelen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 pt-24 pb-16">
      <h1 className="text-3xl font-bold mb-2">Regisztráció</h1>
      <p className="text-gray-600 mb-8">
        Van már fiókod?{" "}
        <Link className="text-blue-700 underline" to="/bejelentkezes">
          Bejelentkezés
        </Link>
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
            {error}
          </div>
        )}
        {info && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 text-sm">
            {info}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium">Keresztnév</span>
            <input
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={form.firstName}
              onChange={(e) => set("firstName", e.target.value)}
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Vezetéknév</span>
            <input
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={form.lastName}
              onChange={(e) => set("lastName", e.target.value)}
              required
            />
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-medium">E-mail</span>
          <input
            type="email"
            required
            autoComplete="email"
            className="mt-1 w-full border rounded-lg px-3 py-2"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium">Telefon</span>
          <input
            type="tel"
            className="mt-1 w-full border rounded-lg px-3 py-2"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+36…"
          />
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium">Jelszó</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Jelszó újra</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className="mt-1 w-full border rounded-lg px-3 py-2"
              value={form.password2}
              onChange={(e) => set("password2", e.target.value)}
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isCompany}
            onChange={(e) => set("isCompany", e.target.checked)}
          />
          Cég nevére regisztrálok
        </label>

        {form.isCompany && (
          <div className="space-y-3 rounded-xl border p-4 bg-gray-50">
            <label className="block">
              <span className="text-sm font-medium">
                Cégnév <span className="text-red-600">*</span>
              </span>
              <input
                required
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.company.name}
                onChange={(e) => setCompany("name", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">
                Adószám <span className="text-red-600">*</span>
              </span>
              <input
                required
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.company.taxNumber}
                onChange={(e) => setCompany("taxNumber", e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">
                EU adószám (VAT){" "}
                <span className="text-gray-500 font-normal">(opcionális)</span>
              </span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.company.vatNumber}
                onChange={(e) => setCompany("vatNumber", e.target.value)}
                placeholder="pl. HU12345678"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">
                Cégjegyzékszám{" "}
                <span className="text-gray-500 font-normal">(opcionális)</span>
              </span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={form.company.companyRegistryNumber}
                onChange={(e) =>
                  setCompany("companyRegistryNumber", e.target.value)
                }
              />
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-[#0c1a26] text-[#f5e9cb] rounded-lg py-2.5 font-medium hover:opacity-95 disabled:opacity-60"
        >
          {busy ? "Regisztráció…" : "Fiók létrehozása"}
        </button>
      </form>
    </div>
  );
}
